import React, { useState, useEffect, useRef } from 'react';
import './DesktopAddPersonModal.css';

/**
 * Simple Add Person Modal
 *
 * Flow:
 * 1. INPUT: Name + Note + Group selection
 * 2. RESEARCHING: Call /api/donor-insight, show spinner
 * 3. RESULTS: Show cards to select from (or single result)
 * 4. NOT_FOUND: No results, option to add anyway
 *
 * On completion: Combine user note + selected research → save → navigate to profile
 */
function DesktopAddPersonModal({
  isOpen,
  onClose,
  groups,
  onSubmit,
  onResearch,
  onCreateGroup,
  preselectedGroupId,
  position,
  prefillName,
  prefillNote,
  isOnboarding = false
}) {
  // Steps: 'input' | 'researching' | 'results' | 'notFound'
  const [step, setStep] = useState('input');
  const [name, setName] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Research results - array of {name, description}
  const [researchCards, setResearchCards] = useState([]);
  const [selectedCardIndex, setSelectedCardIndex] = useState(null);

  // Inline group creation
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupContext, setNewGroupContext] = useState('');
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [createdGroupInModal, setCreatedGroupInModal] = useState(null);

  const nameInputRef = useRef(null);
  const noteInputRef = useRef(null);
  const newGroupInputRef = useRef(null);
  const wasOpenRef = useRef(false);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      wasOpenRef.current = true;
      setStep('input');
      setName(prefillName || '');
      setSelectedGroupId(preselectedGroupId || null);
      setNote(prefillNote || '');
      setIsSubmitting(false);
      setResearchCards([]);
      setSelectedCardIndex(null);
      setShowCreateGroup(false);
      setNewGroupName('');
      setNewGroupContext('');
      setIsCreatingGroup(false);
      setCreatedGroupInModal(null);

      if (!isOnboarding && (!groups || groups.length === 0)) {
        setShowCreateGroup(true);
        setTimeout(() => newGroupInputRef.current?.focus(), 100);
      } else {
        setTimeout(() => {
          if (prefillName) {
            noteInputRef.current?.focus();
          } else {
            nameInputRef.current?.focus();
          }
        }, 100);
      }
    } else if (!isOpen) {
      wasOpenRef.current = false;
    }
  }, [isOpen, preselectedGroupId, prefillName, prefillNote, groups, isOnboarding]);

  // Escape to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Create group inline
  const handleCreateGroup = async () => {
    if (!newGroupName.trim() || isCreatingGroup) return;

    setIsCreatingGroup(true);
    try {
      const newGroup = await onCreateGroup(newGroupName.trim(), newGroupContext.trim());
      if (newGroup?.id) {
        setSelectedGroupId(newGroup.id);
        setCreatedGroupInModal(newGroup);
        setShowCreateGroup(false);
        setNewGroupName('');
        setNewGroupContext('');
        setTimeout(() => nameInputRef.current?.focus(), 100);
      }
    } catch (error) {
      console.error('Failed to create group:', error);
    } finally {
      setIsCreatingGroup(false);
    }
  };

  // Step 1 → Research: User clicks "Next"
  const handleStartResearch = async () => {
    if (!name.trim() || (!isOnboarding && !selectedGroupId)) return;

    setStep('researching');

    try {
      // Call research with name (uses /api/donor-insight - same as Profile button)
      const result = await onResearch(name.trim());

      if (result?.ambiguous && result.options?.length > 0) {
        // Multiple people found - show cards
        setResearchCards(result.options);
        setStep('results');
      } else if (result?.insight) {
        // Single result - show as one card
        setResearchCards([{ name: name.trim(), description: result.insight }]);
        setStep('results');
      } else {
        // No results
        setResearchCards([]);
        setStep('notFound');
      }
    } catch (error) {
      console.error('Research error:', error);
      setResearchCards([]);
      setStep('notFound');
    }
  };

  // Select a research card and add person
  const handleSelectCard = async (index) => {
    setSelectedCardIndex(index);
    setIsSubmitting(true);

    const card = researchCards[index];
    const userNote = note.trim();

    // Combine user note + selected research
    const parts = [];
    if (userNote) parts.push(userNote);
    if (card?.description) parts.push(card.description);
    const combinedNarrative = parts.join('\n\n');

    try {
      await onSubmit(name.trim(), combinedNarrative, selectedGroupId, userNote);
      onClose();
    } catch (error) {
      console.error('Add person error:', error);
      setIsSubmitting(false);
      setSelectedCardIndex(null);
    }
  };

  // Skip research - add with just the note
  const handleSkipResearch = async () => {
    setIsSubmitting(true);
    const userNote = note.trim();

    try {
      await onSubmit(name.trim(), userNote, selectedGroupId, userNote);
      onClose();
    } catch (error) {
      console.error('Add person error:', error);
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      if (step === 'input' && name.trim() && (isOnboarding || selectedGroupId) && !showCreateGroup) {
        handleStartResearch();
      }
    }
  };

  if (!isOpen) return null;

  const selectedGroup = groups?.find(g => g.id === selectedGroupId) || createdGroupInModal;
  const hasGroups = groups && groups.length > 0;
  const canProceed = name.trim() && (isOnboarding || selectedGroupId);

  return (
    <div className="desktop-add-person-backdrop" onClick={handleBackdropClick}>
      <div
        className={`desktop-add-person-modal ${position ? 'positioned' : ''}`}
        style={position ? {
          position: 'fixed',
          ...(position.top !== undefined ? { top: position.top } : {}),
          ...(position.bottom !== undefined ? { bottom: position.bottom } : {}),
          left: position.left,
          maxWidth: `calc(100vw - ${position.left + 20}px)`
        } : undefined}
      >
        {/* Header */}
        <div className="desktop-add-person-header">
          <svg className="desktop-add-person-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <div className="desktop-add-person-title">
            <h3>Add Person</h3>
            <span>
              {step === 'input' && (showCreateGroup ? 'Create a group first' : (isOnboarding ? 'Name and note' : 'Name, note, and group'))}
              {step === 'researching' && `Researching ${name}...`}
              {step === 'results' && (researchCards.length > 1 ? 'Select the right person' : 'Confirm this is correct')}
              {step === 'notFound' && 'No results found'}
            </span>
          </div>
          <button className="desktop-add-person-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="desktop-add-person-content">

          {/* STEP: INPUT */}
          {step === 'input' && (
            <>
              {showCreateGroup ? (
                <div className="desktop-add-person-create-group">
                  <div className="desktop-add-person-create-group-header">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    <span>{hasGroups ? 'Create new group' : 'Create your first group'}</span>
                  </div>
                  <input
                    ref={newGroupInputRef}
                    type="text"
                    className="desktop-add-person-create-group-input"
                    placeholder="Group name (e.g., Board Members, Investors)"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newGroupName.trim()) {
                        handleCreateGroup();
                      }
                    }}
                  />
                  <input
                    type="text"
                    className="desktop-add-person-create-group-input context"
                    placeholder="Context (optional) - helps AI understand who belongs"
                    value={newGroupContext}
                    onChange={(e) => setNewGroupContext(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newGroupName.trim()) {
                        handleCreateGroup();
                      }
                    }}
                  />
                  <div className="desktop-add-person-create-group-actions">
                    {hasGroups && (
                      <button
                        className="desktop-add-person-create-group-cancel"
                        onClick={() => setShowCreateGroup(false)}
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      className="desktop-add-person-create-group-submit"
                      onClick={handleCreateGroup}
                      disabled={!newGroupName.trim() || isCreatingGroup}
                    >
                      {isCreatingGroup ? (
                        <>
                          <div className="desktop-add-person-spinner"></div>
                          Creating...
                        </>
                      ) : (
                        <>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M12 5v14M5 12h14" />
                          </svg>
                          Create Group
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <input
                    ref={nameInputRef}
                    type="text"
                    className="desktop-add-person-name-input"
                    placeholder="Person's name..."
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />

                  <textarea
                    ref={noteInputRef}
                    className="desktop-add-person-note-input"
                    placeholder="Quick note... how you met, why they matter, etc."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    onKeyDown={handleKeyDown}
                    rows={2}
                  />

                  {/* Group Selection - hidden during onboarding */}
                  {!isOnboarding && (
                    createdGroupInModal ? (
                      <div className="desktop-add-person-created-group">
                        <span className="desktop-add-person-group-dot" style={{ background: createdGroupInModal.color || '#a78bfa' }} />
                        <span>Adding to <strong>{createdGroupInModal.name}</strong></span>
                      </div>
                    ) : (
                      <div className="desktop-add-person-group-section">
                        <div className="desktop-add-person-group-label">
                          Add to group <span className="required">*</span>
                        </div>
                        <div className="desktop-add-person-group-options">
                          {groups?.map((group) => (
                            <button
                              key={group.id}
                              className={`desktop-add-person-group-btn ${selectedGroupId === group.id ? 'selected' : ''}`}
                              onClick={() => setSelectedGroupId(group.id)}
                            >
                              <span className="desktop-add-person-group-dot" style={{ background: group.color || '#a78bfa' }} />
                              <span className="desktop-add-person-group-name">{group.name}</span>
                            </button>
                          ))}
                          <button
                            className="desktop-add-person-group-btn create-new"
                            onClick={() => {
                              setShowCreateGroup(true);
                              setTimeout(() => newGroupInputRef.current?.focus(), 100);
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M12 5v14M5 12h14" />
                            </svg>
                            <span className="desktop-add-person-group-name">New Group</span>
                          </button>
                        </div>
                        {!selectedGroupId && name.trim() && (
                          <div className="desktop-add-person-group-hint">
                            Select a group to continue
                          </div>
                        )}
                      </div>
                    )
                  )}
                </>
              )}
            </>
          )}

          {/* STEP: RESEARCHING */}
          {step === 'researching' && (
            <div className="desktop-add-person-researching">
              <div className="desktop-add-person-spinner-large"></div>
              <span>Researching {name}...</span>
              <span className="desktop-add-person-researching-sub">Finding public information</span>
            </div>
          )}

          {/* STEP: RESULTS */}
          {step === 'results' && researchCards.length > 0 && (
            <div className="desktop-add-person-matches">
              {/* User's note preview if they wrote one */}
              {note.trim() && (
                <div className="desktop-add-person-note-preview">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6" />
                  </svg>
                  <span>Your note: "{note.trim().slice(0, 50)}{note.trim().length > 50 ? '...' : ''}"</span>
                </div>
              )}

              <div className="desktop-add-person-match-cards">
                {researchCards.map((card, i) => {
                  const isSelected = selectedCardIndex === i;
                  const displayName = card.name || name;
                  const displayInfo = card.description || card.info || '';

                  return (
                    <button
                      key={i}
                      className={`desktop-add-person-match-card ${isSelected ? 'adding' : ''}`}
                      onClick={() => handleSelectCard(i)}
                      disabled={isSubmitting}
                    >
                      {isSelected ? (
                        <div className="match-card-adding">
                          <div className="desktop-add-person-spinner-large"></div>
                          <span>Adding {displayName}...</span>
                        </div>
                      ) : (
                        <>
                          <div className="match-card-header">
                            <div className="match-card-avatar">
                              {displayName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                            </div>
                            <div className="match-card-name">{displayName}</div>
                          </div>
                          <div className="match-card-info">
                            {displayInfo.slice(0, 200)}{displayInfo.length > 200 ? '...' : ''}
                          </div>
                          {!isOnboarding && selectedGroup && (
                            <div className="match-card-group">
                              <span className="desktop-add-person-group-dot" style={{ background: selectedGroup.color || '#a78bfa' }} />
                              {selectedGroup.name}
                            </div>
                          )}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>

              {!isSubmitting && researchCards.length > 1 && (
                <button
                  className="desktop-add-person-none-match"
                  onClick={() => setStep('notFound')}
                >
                  None of these are right
                </button>
              )}
            </div>
          )}

          {/* STEP: NOT FOUND */}
          {step === 'notFound' && (
            <div className="desktop-add-person-not-found">
              <div className="desktop-add-person-not-found-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
              </div>
              <div className="desktop-add-person-not-found-text">
                Couldn't find public info for <strong>{name}</strong>
              </div>
              {note.trim() && (
                <div className="desktop-add-person-note-will-save">
                  Your note will be saved to their profile
                </div>
              )}
              {!isOnboarding && selectedGroup && (
                <div className="desktop-add-person-preselected-group">
                  <span className="desktop-add-person-group-dot" style={{ background: selectedGroup.color || '#a78bfa' }} />
                  Adding to <strong>{selectedGroup.name}</strong>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="desktop-add-person-footer">
          {step === 'input' && !showCreateGroup && (
            <>
              <span className="desktop-add-person-hint">
                <kbd>⌘</kbd><kbd>Enter</kbd> to continue
              </span>
              <div className="desktop-add-person-actions">
                <button className="desktop-add-person-cancel-btn" onClick={onClose}>
                  Cancel
                </button>
                <button
                  className="desktop-add-person-submit-btn"
                  onClick={handleStartResearch}
                  disabled={!canProceed}
                >
                  Next
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              </div>
            </>
          )}

          {step === 'researching' && (
            <div className="desktop-add-person-actions">
              <button className="desktop-add-person-cancel-btn" onClick={onClose}>
                Cancel
              </button>
            </div>
          )}

          {step === 'results' && (
            <div className="desktop-add-person-actions">
              <button className="desktop-add-person-cancel-btn" onClick={onClose}>
                Cancel
              </button>
            </div>
          )}

          {step === 'notFound' && (
            <div className="desktop-add-person-actions">
              <button className="desktop-add-person-cancel-btn" onClick={onClose}>
                Cancel
              </button>
              <button
                className="desktop-add-person-submit-btn"
                onClick={handleSkipResearch}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="desktop-add-person-spinner"></div>
                    Adding...
                  </>
                ) : (
                  <>
                    Add Anyway
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DesktopAddPersonModal;
